import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  TextField,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Print as PrintIcon,
  PictureAsPdf as PdfIcon,
  Close as CloseIcon,
  Preview as PreviewIcon
} from '@mui/icons-material';

// Helper function to replace the external dependency
const formatNumber = (value: number, useDecimals = true) => {
  const options = useDecimals ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : {};
  return value.toLocaleString('en-IN', options);
};


export interface BarcodePrintTemplateProps {
  open: boolean;
  onClose: () => void;
  inventoryData: {
    _id: string;
    name: string;
    itemCode?: string;
    hsnCode?: string;
    mrp?: number;
    salePrice?: number;
    barCodeImage?: string;
    sale?: {
      price?: number;
      unit?: {
        code?: string;
      };
    };
    purchase?: {
      unit?: {
        code?: string;
      };
    };
  };
  accountId: string;
  companyInfo?: {
    name?: string;
    phone?: string;
    address?: string;
    customerCare?: string;
  };
}

interface PrintLayout {
  id: string;
  name: string;
  itemsPerRow: number;
  itemsPerPage: number;
  itemWidth: string;
  itemHeight: string;
  pageMargin: string;
  fontSize: string;
}

const PRINT_LAYOUTS: PrintLayout[] = [
  {
    id: 'thermal_58mm',
    name: 'Thermal 58mm Receipt',
    itemsPerRow: 1,
    itemsPerPage: 10,
    itemWidth: '58mm',
    itemHeight: '40mm',
    pageMargin: '2mm',
    fontSize: '9pt'
  },
  {
    id: 'thermal_80mm',
    name: 'Thermal 80mm Receipt',
    itemsPerRow: 1,
    itemsPerPage: 8,
    itemWidth: '80mm',
    itemHeight: '45mm',
    pageMargin: '3mm',
    fontSize: '10pt'
  },
  {
    id: 'standard_labels',
    name: 'Standard Labels (4x6)',
    itemsPerRow: 4,
    itemsPerPage: 24,
    itemWidth: '48mm',
    itemHeight: '46mm',
    pageMargin: '5mm',
    fontSize: '8pt'
  },
  {
    id: 'medium_labels',
    name: 'Medium Labels (3x6)',
    itemsPerRow: 3,
    itemsPerPage: 18,
    itemWidth: '62mm',
    itemHeight: '48mm',
    pageMargin: '8mm',
    fontSize: '9pt'
  },
  {
    id: 'large_labels',
    name: 'Large Labels (2x5)',
    itemsPerRow: 2,
    itemsPerPage: 10,
    itemWidth: '90mm',
    itemHeight: '58mm',
    pageMargin: '10mm',
    fontSize: '10pt'
  }
];

const QUANTITY_PRESETS = [12, 24, 36, 48, 60, 100, 200, 300];

const BarcodePrintTemplate: React.FC<BarcodePrintTemplateProps> = ({
  open,
  onClose,
  inventoryData,
  accountId,
  companyInfo
}) => {
  const [quantity, setQuantity] = useState<number>(24);
  const [customQuantity, setCustomQuantity] = useState<string>('');
  const [layout, setLayout] = useState<PrintLayout>(PRINT_LAYOUTS[0]);
  const [showPreview, setShowPreview] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  const handleQuantityChange = (newQuantity: number) => {
    setQuantity(newQuantity);
    setCustomQuantity('');
  };

  const handleCustomQuantityChange = (value: string) => {
    setCustomQuantity(value);
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue > 0) {
      setQuantity(numValue);
    }
  };
  
  const generatePrintHTML = useCallback((): string => {
    const items = Array(quantity).fill(inventoryData);
    const pages: any[][] = [];
    
    for (let i = 0; i < items.length; i += layout.itemsPerPage) {
      pages.push(items.slice(i, i + layout.itemsPerPage));
    }

    const barcodeImageUrl = inventoryData.barCodeImage || '';
    const companyName = companyInfo?.name || "Your Company Name";
    const customerCare = companyInfo?.customerCare || companyInfo?.phone || "Customer Care";
    const companyAddress = companyInfo?.address?.trim() ? companyInfo.address : "Your Company Address, City, State - PIN";
    const saleUnit = inventoryData.sale?.unit?.code || 'Unit';
    const salePrice = inventoryData.sale?.price ?? inventoryData.salePrice;
    const mrpPrice = inventoryData.mrp;
    const isThermalLayout = layout.id.includes('thermal');

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Barcode Print - ${inventoryData.name}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          @page {
            margin: ${isThermalLayout ? '2mm 0 0 2mm' : '0mm'};
            size: ${isThermalLayout ? `${layout.itemWidth} auto` : 'A4'};
          }
          body {
            font-family: ${isThermalLayout ? 'monospace, Arial' : 'Arial'}, sans-serif;
            font-size: ${isThermalLayout ? '9pt' : '10pt'};
            line-height: 1.1;
            ${isThermalLayout ? `width: ${layout.itemWidth}; max-width: ${layout.itemWidth};` : ''}
          }
          .page {
            width: 100%;
            page-break-after: ${isThermalLayout ? 'auto' : 'always'};
            display: ${isThermalLayout ? 'block' : 'flex'};
            ${isThermalLayout ? '' : 'flex-wrap: wrap;'}
            gap: ${isThermalLayout ? '0mm' : '2mm'};
            ${isThermalLayout ? '' : 'justify-content: flex-start; align-content: flex-start;'}
            padding: ${isThermalLayout ? '0mm' : '3mm'};
          }
          .page:last-child { page-break-after: avoid; }
          .barcode-label {
            width: ${layout.itemWidth};
            height: ${layout.itemHeight};
            border: 2px solid #000;
            padding: ${isThermalLayout ? '3mm' : '2mm'};
            margin: ${isThermalLayout ? '2mm 0' : '0'};
            display: flex;
            flex-direction: column;
            background: white;
            font-size: ${layout.fontSize};
            line-height: 1.2;
            position: relative;
            overflow: hidden;
            break-inside: avoid;
            page-break-inside: avoid;
            box-sizing: border-box;
            flex-shrink: 0;
          }
          .company-name {
            font-weight: bold;
            font-size: ${layout.fontSize === '8pt' ? '9pt' : layout.fontSize === '9pt' ? '10pt' : '11pt'};
            text-align: center; margin-bottom: 1mm; text-transform: uppercase;
            overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          }
          .product-name {
            font-size: ${layout.fontSize}; 
            text-align: center; 
            margin: 0.5mm 0 1.5mm 0; 
            font-weight: 500;
            line-height: 1.2; 
            min-height: ${layout.fontSize === '8pt' ? '16px' : layout.fontSize === '9pt' ? '18px' : '20px'};
            max-height: ${layout.fontSize === '8pt' ? '24px' : layout.fontSize === '9pt' ? '28px' : '32px'};
            overflow: hidden; 
            text-overflow: ellipsis; 
            display: -webkit-box; 
            -webkit-line-clamp: 2; 
            -webkit-box-orient: vertical;
            word-break: break-word;
          }
          .barcode-container {
            display: flex; 
            flex-direction: column; 
            align-items: center;
            margin: 1mm 0; 
            flex: 1; 
            justify-content: center;
            min-height: 0; /* Allows flex item to shrink below content size */
          }
          .barcode-image {
            height: ${layout.fontSize === '8pt' ? '8mm' : layout.fontSize === '9pt' ? '10mm' : '12mm'};
            width: auto; max-width: 95%; object-fit: contain;
          }
          .barcode-number { font-size: 8pt; font-weight: bold; letter-spacing: 0.5px; margin-top: 0.5mm; text-align: center; }
          .product-details { 
            font-size: 7pt; 
            line-height: 1.3; 
            margin-top: auto;
            padding-top: 1mm;
          }
          .detail-row { 
            display: flex; 
            justify-content: space-between; 
            margin: 0.5mm 0; 
            align-items: center;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .quantity-unit { font-size: 7pt; font-weight: 500; }
          .mrp { font-weight: bold; font-size: 7.5pt; }
          .customer-care { font-size: 6.5pt; }
          .tax-info { font-size: 6.5pt; font-style: italic; }
          .address { font-size: 6pt; text-align: center; margin-top: 0.5mm; line-height: 1.1; }
          .no-barcode {
            display: flex; align-items: center; justify-content: center; height: 8mm;
            border: 1px dashed #999; color: #999; font-size: 6pt; background: #f9f9f9; margin: 1mm 0;
          }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        ${pages.map(pageItems => `
          <div class="page">
            ${pageItems.map(item => `
              <div class="barcode-label">
                <!-- <div class="company-name">${companyName}</div> -->
                <div class="product-name">${item.name}</div>
                <div class="barcode-container">
                  ${barcodeImageUrl ? `
                    <img src="${barcodeImageUrl}?t=${Date.now()}" alt="Barcode" class="barcode-image" />
                    <div class="barcode-number">${item.itemCode || '1234567890'}</div>
                  ` : `
                    <div class="no-barcode">No Barcode Available</div>
                    <div class="barcode-number">${item.itemCode || 'N/A'}</div>
                  `}
                </div>
                <div class="product-details">
                  <div class="detail-row">
                    <span class="quantity-unit">Qty: 1 ${saleUnit}</span>
                    <span class="mrp">MRP: ₹${formatNumber(mrpPrice || 0, false)}</span>
                  </div>
                  <div class="detail-row">
                    <span class="quantity-unit">Sale Price: ₹${formatNumber(salePrice || 0, false)}</span>
                    <span class="tax-info">(Incl.All taxes)</span>
                  </div>
                  <div class="detail-row">
                    <span class="customer-care">C.care: ${customerCare}</span>
                  </div>
                </div>
                <!-- <div class="address">${companyAddress}</div> -->
              </div>
            `).join('')}
          </div>
        `).join('')}
      </body>
      </html>
    `;
  }, [quantity, layout, inventoryData, companyInfo]);

  const performPrintAction = useCallback(async (setLoadingState: (loading: boolean) => void) => {
    setLoadingState(true);
    try {
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.left = '-9999px';
      
      document.body.appendChild(iframe);
      
      const printContent = generatePrintHTML();
      
      if (iframe.contentWindow) {
        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(printContent);
        doc.close();
      } else {
        console.error('Failed to access iframe content window');
        return;
      }

      iframe.onload = () => {
        setTimeout(() => {
          if (iframe.contentWindow) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            
            setTimeout(() => {
              document.body.removeChild(iframe);
              setLoadingState(false);
            }, 500);
          }
        }, 500); // Wait for images/styles to render
      };

      iframe.onerror = () => {
        console.error('Failed to load iframe content for printing.');
        if (iframe.parentNode) {
            document.body.removeChild(iframe);
        }
        setLoadingState(false);
      };

    } catch (error) {
      console.error('Print error:', error);
      alert('Failed to initiate print. Please check the console for errors.');
      setLoadingState(false);
    }
  }, [generatePrintHTML]);

  const handlePrint = () => performPrintAction(setIsPrinting);
  const handleSavePDF = () => performPrintAction(setIsGeneratingPdf);
  
  const renderPreview = () => {
    if (!showPreview) return null;

    const previewItems = Math.min(quantity, 6);
    const companyName = companyInfo?.name || "Your Company Name";
    const customerCare = companyInfo?.customerCare || companyInfo?.phone || "Customer Care";
    const companyAddress = companyInfo?.address?.trim() ? companyInfo.address : "Your Company Address, City, State - PIN";
    const saleUnit = inventoryData.sale?.unit?.code || 'Unit';
    const salePrice = inventoryData.sale?.price ?? inventoryData.salePrice;
    const mrpPrice = inventoryData.mrp;
    
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Preview (showing {previewItems} of {quantity} items)
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            p: 2,
            border: '1px solid #ddd',
            borderRadius: 1,
            backgroundColor: '#fafafa',
            maxHeight: '400px',
            overflow: 'auto'
          }}
        >
          {Array(previewItems).fill(inventoryData).map((item, index) => (
            <Box 
              key={index} 
              sx={{
                border: '2px solid #000',
                p: '2mm',
                backgroundColor: 'white',
                display: 'flex',
                flexDirection: 'column',
                width: layout.itemWidth,
                minHeight: layout.itemHeight,
                fontSize: layout.fontSize,
                lineHeight: 1.2,
                fontFamily: 'Arial, sans-serif'
              }}
            >
              {/* <Typography sx={{
                fontWeight: 'bold',
                fontSize: layout.fontSize === '8pt' ? '9pt' : layout.fontSize === '9pt' ? '10pt' : '11pt',
                textAlign: 'center',
                textTransform: 'uppercase',
                mb: '1mm',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {companyName}
              </Typography> */}
              <Typography sx={{
                fontSize: layout.fontSize,
                textAlign: 'center',
                fontWeight: 500,
                my: '0.5mm',
                mb: '1.5mm',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {item.name}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, justifyContent: 'center', my: '1mm' }}>
                {item.barCodeImage ? (
                  <>
                    <img
                      src={item.barCodeImage}
                      alt="Barcode"
                      style={{
                        height: layout.fontSize === '8pt' ? '8mm' : layout.fontSize === '9pt' ? '10mm' : '12mm',
                        width: 'auto',
                        maxWidth: '95%',
                        objectFit: 'contain'
                      }}
                    />
                    <Typography sx={{ fontSize: '8pt', fontWeight: 'bold', letterSpacing: '0.5px', mt: '0.5mm' }}>
                      {item.itemCode || '1234567890'}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Box sx={{ border: '1px dashed #999', color: '#999', fontSize: '6pt', p: '1mm', textAlign: 'center', width: '100%' }}>
                      No Barcode Available
                    </Box>
                    <Typography sx={{ fontSize: '8pt', fontWeight: 'bold', mt: '0.5mm' }}>
                      {item.itemCode || 'N/A'}
                    </Typography>
                  </>
                )}
              </Box>
              <Box sx={{ fontSize: '7pt', lineHeight: 1.3, mt: 'auto', pt: '1mm' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '0.5mm' }}>
                  <Typography sx={{ fontSize: '7pt', fontWeight: 500 }}>Qty: 1 {saleUnit}</Typography>
                  <Typography sx={{ fontSize: '7.5pt', fontWeight: 'bold' }}>MRP: ₹{formatNumber(mrpPrice || 0, false)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '0.5mm' }}>
                  <Typography sx={{ fontSize: '7pt', fontWeight: 500 }}>Sale Price: ₹{formatNumber(salePrice || 0, false)}</Typography>
                  <Typography sx={{ fontSize: '6.5pt', fontStyle: 'italic' }}>(Incl.All taxes)</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '0.5mm' }}>
                  <Typography sx={{ fontSize: '6.5pt' }}>C.care: {customerCare}</Typography>
                </Box>
              </Box>
              {/* <Typography sx={{ fontSize: '6pt', textAlign: 'center', mt: '0.5mm', lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {companyAddress}
              </Typography> */}
            </Box>
          ))}
        </Box>
        {quantity > previewItems && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            ... and {quantity - previewItems} more items
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Print Barcodes</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>{inventoryData.name}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}><Typography variant="body2" color="text.secondary">Item Code</Typography><Typography variant="body2">{inventoryData.itemCode || '-'}</Typography></Grid>
              <Grid item xs={6} sm={3}><Typography variant="body2" color="text.secondary">HSN Code</Typography><Typography variant="body2">{inventoryData.hsnCode || '-'}</Typography></Grid>
              <Grid item xs={6} sm={3}><Typography variant="body2" color="text.secondary">MRP</Typography><Typography variant="body2">{inventoryData.mrp ? ` ${formatNumber(inventoryData.mrp)}` : '-'}</Typography></Grid>
              <Grid item xs={6} sm={3}><Typography variant="body2" color="text.secondary">Sale Price</Typography><Typography variant="body2">{inventoryData.salePrice ? ` ${formatNumber(inventoryData.salePrice)}` : '-'}</Typography></Grid>
            </Grid>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Select Quantity</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                {QUANTITY_PRESETS.map((preset) => (
                  <Chip
                    key={preset}
                    label={preset.toString()}
                    variant={quantity === preset && !customQuantity ? "filled" : "outlined"}
                    color={quantity === preset && !customQuantity ? "primary" : "default"}
                    onClick={() => handleQuantityChange(preset)}
                    sx={{ mb: 1 }}
                  />
                ))}
              </Stack>
              <TextField
                label="Custom Quantity" type="number" size="small"
                value={customQuantity}
                onChange={(e) => handleCustomQuantityChange(e.target.value)}
                inputProps={{ min: 1, max: 10000 }}
                sx={{ mb: 2, width: '200px' }}
              />
              <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                Total Items to Print: {quantity}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Print Layout</Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Layout</InputLabel>
                <Select
                  value={layout.id}
                  label="Layout"
                  onChange={(e) => {
                    const selectedLayout = PRINT_LAYOUTS.find(l => l.id === e.target.value);
                    if (selectedLayout) setLayout(selectedLayout);
                  }}
                >
                  {PRINT_LAYOUTS.map((l) => (<MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>))}
                </Select>
              </FormControl>
              {/* <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Layout Details:</strong><br />
                  • Items per row: {layout.itemsPerRow}<br />
                  • Items per page: {layout.itemsPerPage}<br />
                  • Pages needed: {Math.ceil(quantity / layout.itemsPerPage)}
                </Typography>
              </Box> */}
            </Grid>
          </Grid>

          <Box sx={{ mt: 1 , textAlign: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<PreviewIcon />}
              onClick={() => setShowPreview(!showPreview)}
            >
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </Button>
          </Box>
          {renderPreview()}
          {!inventoryData.barCodeImage && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This item doesn't have a barcode generated yet. The printout will show placeholders.
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button
          variant="outlined"
          startIcon={isGeneratingPdf ? <CircularProgress size={16} /> : <PdfIcon />}
          onClick={handleSavePDF}
          disabled={isGeneratingPdf || isPrinting}
        >
          Save as PDF
        </Button>
        <Button
          variant="contained"
          startIcon={isPrinting ? <CircularProgress size={16} /> : <PrintIcon />}
          onClick={handlePrint}
          disabled={isPrinting || isGeneratingPdf}
        >
          Print Now
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BarcodePrintTemplate;

